// components/PeriodDropdown/SimplePeriodDropdown.tsx
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Box, Button, Typography, Popper, Paper, MenuList, MenuItem, ClickAwayListener } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import dayjs from 'dayjs';

import { DateRangeRsuite } from '../DateRange/Date';
import type {  SubPeriodOption, DateRange, Props, ParentWithSub } from './types';
import { getPeriodOptions, MONTHS, QUARTERS, HALVES, CAL_MONTH_LABELS } from './constants';
import { getDateRange, formatDateRange, toUnix, dayRangeForTs, normalizeRangeToSeconds } from './utils';
import { useFetchOpeningBalanceDateQuery } from '../../../../redux/apis/openingbalacedate.api';
import './SimplePeriodDropdown.css';
import { useAppSelector } from '../../../../redux/hooks/useAppSelector';
// import { useSelector } from 'react-redux';

// ---- Reusable debounce hook (typed, no stale closures) ----
function useDebouncedCallback<A extends unknown[]>(fn: (...args: A) => void, delay: number) {
  const fnRef = useRef(fn);
  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const debounced = useCallback(
    (...args: A) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        fnRef.current(...args);
      }, delay);
    },
    [delay]
  );

  useEffect(() => cancel, [cancel]);

  return [debounced, cancel] as const;
}

export const SimplePeriodDropdown: React.FC<Props> = ({
  value,
  onChange,
  title = 'Period:',
  showDateText = true,
  includeToday = true,
  useFYFromRedux = true,
  monthIdsAreCalendar = true,
  selectedWhenNull = false,
  type = 1,
}) => {
  console.log({value,type})
  const [period, setPeriod] = useState<'today' | 'tilltoday' | 'thisWeek' | 'year' | 'month' | 'quarter' | 'half' | 'custom'>('custom');
  const [subPeriod, setSubPeriod] = useState<SubPeriodOption[]>([]);
  const [mainAnchorEl, setMainAnchorEl] = useState<null | HTMLElement>(null);
  const [subAnchorEl, setSubAnchorEl] = useState<null | HTMLElement>(null);
  const [mainIndex, setMainIndex] = useState<number>(-1);
  const [subIndex, setSubIndex] = useState<number>(-1);
  const [openSubParent, setOpenSubParent] = useState<ParentWithSub | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Local editing state for custom range, as a tuple [fromMs, toMs]
  type RsuiteTuple = [number | null, number | null];
  const [customMs, setCustomMs] = useState<RsuiteTuple | null>(null);

  const mainRefs = useRef<(HTMLLIElement | null)[]>([]);
  const subRefs = useRef<(HTMLLIElement | null)[]>([]);

  // Fetch opening balance date
  const { data: openingBalanceDate } = useFetchOpeningBalanceDateQuery(
    {
      invoicetypecd: 'Sales',
      invoiceid: 0,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  // --- FY from Redux ---
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const financialYear = useAppSelector((state) => state?.userPrefrenceandReducer?.financialYear);
  const rawFyStart: number | null = typeof financialYear?.startdate === 'number' ? financialYear.startdate : null;
  const rawFyEnd: number | null = typeof financialYear?.enddate === 'number' ? financialYear.enddate : null;
  const baseFyStart = typeof rawFyStart === 'number' ? (rawFyStart > 1e12 ? Math.floor(rawFyStart / 1000) : rawFyStart) : null;
  const fyEnd = typeof rawFyEnd === 'number' ? (rawFyEnd > 1e12 ? Math.floor(rawFyEnd / 1000) : rawFyEnd) : null;

  // Calculate opening balance date in seconds (start of day)
  const openingBalanceSec = openingBalanceDate?.getMinMaxInvoiceDate?.openingbalancedate
    ? dayjs(openingBalanceDate.getMinMaxInvoiceDate.openingbalancedate).startOf('day').unix()
    : null;

  // Use opening balance date as fyStart if available, otherwise use normal fyStart
  const fyStart = openingBalanceSec !== null ? openingBalanceSec : baseFyStart;
  const fyReady = typeof fyStart === 'number' && typeof fyEnd === 'number';

  const nowSec = Math.floor(Date.now() / 1000);
  const todayInsideFY = fyReady ? nowSec >= (fyStart as number) && nowSec <= (fyEnd as number) : true;

  // Calculate this week range (Monday to Sunday)
  const thisWeekRange = useMemo(() => {
    const now = dayjs();
    const startOfWeek = now.startOf('week'); // Monday
    const endOfWeek = now.endOf('week'); // Sunday
    return {
      from: Math.floor(startOfWeek.startOf('day').unix()),
      to: Math.floor(endOfWeek.endOf('day').unix()),
    };
  }, []);

  const thisWeekInsideFY = fyReady
    ? thisWeekRange.to >= (fyStart as number) && thisWeekRange.from <= (fyEnd as number)
    : true;
  const thisWeekAfterOpeningBalance = fyReady && openingBalanceSec
    ? thisWeekRange.to >= openingBalanceSec
    : true;

  // Filter menu options based on opening balance date; labels come from getPeriodOptions(type)
  const menuOptions = useMemo(() => {
    return getPeriodOptions(type).filter((option) => {
      if (option.id === 'today' || option.id === 'tilltoday') {
        return includeToday && todayInsideFY && (openingBalanceSec === null || nowSec >= openingBalanceSec);
      }
      if (option.id === 'thisWeek') {
        return thisWeekInsideFY && thisWeekAfterOpeningBalance;
      }
      return true;
    });
  }, [type, includeToday, todayInsideFY, openingBalanceSec, nowSec, thisWeekInsideFY, thisWeekAfterOpeningBalance]);

  // Month submenu options (FY order if requested)
  const getMonthOptions = (): SubPeriodOption[] => {
    if (monthIdsAreCalendar || !fyReady) return MONTHS; // Jan..Dec ids 1..12
    const fyStartMonth = new Date((fyStart as number) * 1000).getMonth(); // 0..11
    const arr: SubPeriodOption[] = [];
    for (let i = 0; i < 12; i++) {
      const mIndex = (fyStartMonth + i) % 12;
      arr.push({ id: i + 1, label: CAL_MONTH_LABELS[mIndex].label });
    }
    return arr;
  };

  const getSubPeriodOptions = (id: string): SubPeriodOption[] => {
    switch (id) {
      case 'month':
        return getMonthOptions();
      case 'quarter':
        return QUARTERS;
      case 'half':
        return HALVES;
      default:
        return [];
    }
  };

  // ---------- FY helpers ----------
  const clampToFY = (from: number, to: number) => ({
    from: Math.max(from, fyStart as number),
    to: Math.min(to, fyEnd as number),
  });

  const rangeForFYWindow = (startMonthOffset: number, monthsLen: number) => {
    const base = new Date((fyStart as number) * 1000);
    const baseYear = base.getFullYear();
    const baseMonth = base.getMonth(); // 0..11
    const targetMonthAbs = baseMonth + startMonthOffset;
    const targetYear = baseYear + Math.floor(targetMonthAbs / 12);
    const targetMonth = ((targetMonthAbs % 12) + 12) % 12;
    const start = new Date(targetYear, targetMonth, 1, 0, 0, 0, 0);
    const end = new Date(targetYear, targetMonth + monthsLen, 0, 23, 59, 59, 999);
    return clampToFY(toUnix(start), toUnix(end));
  };

  const calendarIdToFyOffset = (calendarId: number) => {
    const fyStartMonth = new Date((fyStart as number) * 1000).getMonth(); // 0..11
    const calMonth = calendarId - 1; // 0..11
    return (calMonth - fyStartMonth + 12) % 12; // 0..11
  };

  const rangeForFYMonthId = (id: number, useCalendarIds: boolean) => {
    const offset = useCalendarIds ? calendarIdToFyOffset(id) : id - 1;
    return rangeForFYWindow(offset, 1);
  };

  const rangeForFYQuarter = (qid: string) => {
    const map: Record<string, number> = { Q1: 0, Q2: 3, Q3: 6, Q4: 9 };
    return rangeForFYWindow(map[qid] ?? 0, 3);
  };

  const rangeForFYHalf = (hid: string) => {
    const map: Record<string, number> = { H1: 0, H2: 6 };
    return rangeForFYWindow(map[hid] ?? 0, 6);
  };

  // Adjust range to start from opening balance date if the period contains it
  const adjustRangeForOpeningBalance = (range: DateRange): DateRange => {
    if (!openingBalanceSec || !range.from || !range.to) return range;
    // If opening balance date is within this period, start from opening balance date
    if (openingBalanceSec >= range.from && openingBalanceSec <= range.to) {
      return { from: openingBalanceSec, to: range.to };
    }
    return range;
  };

  // ---------- Canonical range calculators (both FY + generic) ----------
  const computeRangeFY = (
    periodId: 'today' | 'tilltoday' | 'thisWeek' | 'year' | 'month' | 'quarter' | 'half' | 'custom',
    subId?: string | number,
    useCalendarIdsForMonth = monthIdsAreCalendar
  ): DateRange => {
    if (!fyReady) return { from: null, to: null };
    let range: DateRange;
    switch (periodId) {
      case 'year':
        range = { from: fyStart as number, to: fyEnd as number };
        break;
      case 'today': {
        // Today = start of today to end of today (single day)
        const clamped = Math.min(Math.max(nowSec, fyStart as number), fyEnd as number);
        const d = dayRangeForTs(clamped);
        range = clampToFY(d.from, d.to);
        break;
      }
      case 'tilltoday': {
        // Till Today = FY start to end of today
        const todayEndSec = Math.floor(dayjs().endOf('day').unix());
        range = clampToFY(fyStart as number, Math.min(todayEndSec, fyEnd as number));
        break;
      }
      case 'thisWeek': {
        const clampedFrom = Math.max(thisWeekRange.from, fyStart as number);
        const clampedTo = Math.min(thisWeekRange.to, fyEnd as number);
        range = { from: clampedFrom, to: clampedTo };
        break;
      }
      case 'month': {
        const m = Number(subId);
        if (!m || m < 1 || m > 12) {
          range = { from: fyStart as number, to: fyEnd as number };
        } else {
          range = rangeForFYMonthId(m, useCalendarIdsForMonth);
        }
        break;
      }
      case 'quarter':
        range = rangeForFYQuarter(String(subId));
        break;
      case 'half':
        range = rangeForFYHalf(String(subId));
        break;
      default:
        return { from: null, to: null };
    }
    return adjustRangeForOpeningBalance(range);
  };

  const computeRangeGeneric = (periodId: 'today' | 'tilltoday' | 'thisWeek' | 'year' | 'month' | 'quarter' | 'half' | 'custom', subId?: string | number): DateRange => {
    let range: DateRange;
    if (periodId === 'tilltoday' && fyReady) {
      // Till Today = FY start to end of today
      const todayEndSec = Math.floor(dayjs().endOf('day').unix());
      range = clampToFY(fyStart as number, Math.min(todayEndSec, fyEnd as number));
    } else if (periodId === 'thisWeek') {
      range = thisWeekRange;
    } else {
      range = normalizeRangeToSeconds(getDateRange(periodId, subId));
    }
    return adjustRangeForOpeningBalance(range);
  };

  // Memoized disabled state for all sub-periods - recalculates only when fyStart/fyEnd/openingBalanceSec changes
  const disabledSubItemsMemoized = useMemo(() => {
    // Use openingBalanceSec if available, otherwise use fyStart as fallback for the start threshold
    const effectiveStart = openingBalanceSec !== null ? openingBalanceSec : (fyReady ? (fyStart as number) : null);
    
    if (!fyReady || effectiveStart === null || fyEnd === null) {
      console.log('[SimplePeriodDropdown] Disabled check skipped:', { 
        fyReady, 
        effectiveStart, 
        fyEnd,
        openingBalanceSec, 
        fyStart 
      });
      return {
        months: {} as Record<number, boolean>,
        quarters: {} as Record<string, boolean>,
        halves: {} as Record<string, boolean>,
      };
    }

    const monthsDisabled: Record<number, boolean> = {};
    const quartersDisabled: Record<string, boolean> = {};
    const halvesDisabled: Record<string, boolean> = {};

    // Check all months (1-12) - enable if period overlaps with [effectiveStart, fyEnd]
    // A period overlaps if: period.from <= fyEnd && period.to >= effectiveStart
    for (let m = 1; m <= 12; m++) {
      // Use the calendar interpretation for months (simpler and matches user expectation)
      const range = useFYFromRedux && fyReady
        ? rangeForFYMonthId(m, monthIdsAreCalendar)
        : computeRangeGeneric('month', m);
      
      // Enable if period overlaps with [effectiveStart, fyEnd]
      const overlaps = range.from !== null && range.to !== null && 
        range.from <= fyEnd && range.to >= effectiveStart;
      monthsDisabled[m] = !overlaps;
      
      if (m <= 3) {
        // Debug first 3 months
        console.log(`[SimplePeriodDropdown] Month ${m}:`, {
          range: { from: range.from, to: range.to },
          effectiveStart,
          fyEnd,
          disabled: monthsDisabled[m],
          overlaps,
          fromDate: range.from ? new Date(range.from * 1000).toISOString() : null,
          toDate: range.to ? new Date(range.to * 1000).toISOString() : null,
          startDate: new Date(effectiveStart * 1000).toISOString(),
          endDate: new Date(fyEnd * 1000).toISOString(),
        });
      }
    }

    // Check all quarters - enable if period overlaps with [effectiveStart, fyEnd]
    // Quarters are calendar-based: Q1=Apr-Jun, Q2=Jul-Sep, Q3=Oct-Dec, Q4=Jan-Mar
    (['Q1', 'Q2', 'Q3', 'Q4'] as const).forEach((q) => {
      let range: DateRange;
      
      if (useFYFromRedux && fyReady) {
        // Calculate calendar quarters using dayjs
        const fyStartDate = dayjs.unix(effectiveStart);
        const fyEndDate = dayjs.unix(fyEnd);
        const fyStartYear = fyStartDate.year();
        
        switch (q) {
          case 'Q1': // Apr-Jun
            range = {
              from: toUnix(dayjs(`${fyStartYear}-04-01`).startOf('day').toDate()),
              to: toUnix(dayjs(`${fyStartYear}-06-30`).endOf('day').toDate()),
            };
            break;
          case 'Q2': // Jul-Sep
            range = {
              from: toUnix(dayjs(`${fyStartYear}-07-01`).startOf('day').toDate()),
              to: toUnix(dayjs(`${fyStartYear}-09-30`).endOf('day').toDate()),
            };
            break;
          case 'Q3': // Oct-Dec
            range = {
              from: toUnix(dayjs(`${fyStartYear}-10-01`).startOf('day').toDate()),
              to: toUnix(dayjs(`${fyStartYear}-12-31`).endOf('day').toDate()),
            };
            break;
          case 'Q4': // Jan-Mar (next year)
            const fyEndYear = fyEndDate.year();
            range = {
              from: toUnix(dayjs(`${fyEndYear}-01-01`).startOf('day').toDate()),
              to: toUnix(dayjs(`${fyEndYear}-03-31`).endOf('day').toDate()),
            };
            break;
        }
        // Clamp to FY boundaries
        range = clampToFY(range.from ?? 0, range.to ?? 0);
      } else {
        range = computeRangeGeneric('quarter', q);
      }
      
      const overlaps = range.from !== null && range.to !== null && 
        range.from <= fyEnd && range.to >= effectiveStart;
      quartersDisabled[q] = !overlaps;
      
      // Debug all quarters
      console.log(`[SimplePeriodDropdown] ${q} calculation:`, {
        range: { from: range.from, to: range.to },
        fromDate: range.from ? dayjs.unix(range.from).format('YYYY-MM-DD') : null,
        toDate: range.to ? dayjs.unix(range.to).format('YYYY-MM-DD') : null,
        effectiveStart,
        effectiveStartDate: dayjs.unix(effectiveStart).format('YYYY-MM-DD'),
        fyEnd,
        fyEndDate: dayjs.unix(fyEnd).format('YYYY-MM-DD'),
        overlaps,
        disabled: quartersDisabled[q],
      });
    });

    // Check all halves - enable if period overlaps with [effectiveStart, fyEnd]
    // Halves are calendar-based: H1=Apr-Sep, H2=Oct-Mar
    (['H1', 'H2'] as const).forEach((h) => {
      let range: DateRange;
      
      if (useFYFromRedux && fyReady) {
        // Calculate calendar halves using dayjs
        const fyStartDate = dayjs.unix(effectiveStart);
        const fyEndDate = dayjs.unix(fyEnd);
        const fyStartYear = fyStartDate.year();
        const fyEndYear = fyEndDate.year();
        
        if (h === 'H1') {
          // H1: Apr-Sep
          range = {
            from: toUnix(dayjs(`${fyStartYear}-04-01`).startOf('day').toDate()),
            to: toUnix(dayjs(`${fyStartYear}-09-30`).endOf('day').toDate()),
          };
        } else {
          // H2: Oct-Mar (spans to next year)
          range = {
            from: toUnix(dayjs(`${fyStartYear}-10-01`).startOf('day').toDate()),
            to: toUnix(dayjs(`${fyEndYear}-03-31`).endOf('day').toDate()),
          };
        }
        // Clamp to FY boundaries
        range = clampToFY(range.from ?? 0, range.to ?? 0);
      } else {
        range = computeRangeGeneric('half', h);
      }
      
      const overlaps = range.from !== null && range.to !== null && 
        range.from <= fyEnd && range.to >= effectiveStart;
      halvesDisabled[h] = !overlaps;
    });

    console.log('[SimplePeriodDropdown] Disabled states:', {
      effectiveStart,
      effectiveStartDate: new Date(effectiveStart * 1000).toISOString(),
      fyEnd,
      fyEndDate: new Date(fyEnd * 1000).toISOString(),
      months: monthsDisabled,
      quarters: quartersDisabled,
      halves: halvesDisabled,
    });

    return {
      months: monthsDisabled,
      quarters: quartersDisabled,
      halves: halvesDisabled,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fyStart, fyEnd, fyReady, openingBalanceSec, useFYFromRedux, monthIdsAreCalendar]);
  // ---------- equality + inference ----------
  const sameRange = (a?: DateRange, b?: DateRange) =>
    !!a && !!b && a.from != null && a.to != null && b.from != null && b.to != null && a.from === b.from && a.to === b.to;

  const inferFromValue = (raw?: DateRange) => {
    const v = normalizeRangeToSeconds(raw);
    if (v.from == null || v.to == null) return { period: 'custom' as const };

    const cands: Array<{ period: 'today' | 'tilltoday' | 'thisWeek' | 'year' | 'month' | 'quarter' | 'half'; subId?: string | number; range: DateRange }> = [];

    if (useFYFromRedux && fyReady) {
      cands.push({ period: 'year', range: computeRangeFY('year') });
      if (todayInsideFY && (openingBalanceSec === null || nowSec >= openingBalanceSec)) {
        if (type === 2) {
          cands.push({ period: 'tilltoday', range: computeRangeFY('tilltoday') });
        } else {
          cands.push({ period: 'today', range: computeRangeFY('today') });
        }
      }
      if (thisWeekInsideFY && thisWeekAfterOpeningBalance) {
        cands.push({ period: 'thisWeek', range: computeRangeFY('thisWeek') });
      }
      for (let m = 1; m <= 12; m++) {
        cands.push({ period: 'month', subId: m, range: computeRangeFY('month', m, true) });
        cands.push({ period: 'month', subId: m, range: computeRangeFY('month', m, false) });
      }
      (['Q1', 'Q2', 'Q3', 'Q4'] as const).forEach((q) => cands.push({ period: 'quarter', subId: q, range: computeRangeFY('quarter', q) }));
      (['H1', 'H2'] as const).forEach((h) => cands.push({ period: 'half', subId: h, range: computeRangeFY('half', h) }));
    } else {
      cands.push({ period: 'year', range: computeRangeGeneric('year') });
      if (includeToday && (openingBalanceSec === null || nowSec >= openingBalanceSec)) {
        if (type === 2) {
          cands.push({ period: 'tilltoday', range: computeRangeGeneric('tilltoday') });
        } else {
          cands.push({ period: 'today', range: computeRangeGeneric('today') });
        }
      }
      if (thisWeekInsideFY && thisWeekAfterOpeningBalance) {
        cands.push({ period: 'thisWeek', range: computeRangeGeneric('thisWeek') });
      }
      for (let m = 1; m <= 12; m++) cands.push({ period: 'month', subId: m, range: computeRangeGeneric('month', m) });
      (['Q1', 'Q2', 'Q3', 'Q4'] as const).forEach((q) =>
        cands.push({ period: 'quarter', subId: q, range: computeRangeGeneric('quarter', q) })
      );
      (['H1', 'H2'] as const).forEach((h) => cands.push({ period: 'half', subId: h, range: computeRangeGeneric('half', h) }));
    }

    const hit = cands.find((c) => sameRange(v, c.range));
    if (hit) return { period: hit.period, subId: hit.subId };
    return { period: 'custom' as const };
  };

  /** Infer subId for currently open parent from `value` (no persistence). */
  const inferSubIdForParent = (parent: ParentWithSub, v: DateRange): string | number | undefined => {
    const vv = normalizeRangeToSeconds(v);
    if (vv.from == null || vv.to == null) return undefined;

    const tryList: Array<{ id: string | number; range: DateRange }> = [];

    if (useFYFromRedux && fyReady) {
      if (parent === 'month') {
        for (let m = 1; m <= 12; m++) tryList.push({ id: m, range: computeRangeFY('month', m, monthIdsAreCalendar) });
      } else if (parent === 'quarter') {
        (['Q1', 'Q2', 'Q3', 'Q4'] as const).forEach((q) => tryList.push({ id: q, range: computeRangeFY('quarter', q) }));
      } else if (parent === 'half') {
        (['H1', 'H2'] as const).forEach((h) => tryList.push({ id: h, range: computeRangeFY('half', h) }));
      }
    } else {
      if (parent === 'month') {
        for (let m = 1; m <= 12; m++) tryList.push({ id: m, range: computeRangeGeneric('month', m) });
      } else if (parent === 'quarter') {
        (['Q1', 'Q2', 'Q3', 'Q4'] as const).forEach((q) => tryList.push({ id: q, range: computeRangeGeneric('quarter', q) }));
      } else if (parent === 'half') {
        (['H1', 'H2'] as const).forEach((h) => tryList.push({ id: h, range: computeRangeGeneric('half', h) }));
      }
    }

    const hit = tryList.find((c) => sameRange(vv, c.range));
    return hit?.id;
  };

  // ---------- validate `value` vs FY; then infer selection ----------
  useEffect(() => {
    const v = normalizeRangeToSeconds(value);
    
    // Handle selectedWhenNull prop when value is null/null
    if ((v.from == null || v.to == null) && typeof selectedWhenNull === 'string' && selectedWhenNull !== '') {
      const validPeriodIds = ['today', 'tilltoday', 'thisWeek', 'year', 'month', 'quarter', 'half', 'custom'];
      if (validPeriodIds.includes(selectedWhenNull)) {
        const periodId = selectedWhenNull as 'today' | 'tilltoday' | 'thisWeek' | 'year' | 'month' | 'quarter' | 'half' | 'custom';
        
        // For periods without submenus, just set the period
        if (periodId === 'today' || periodId === 'tilltoday' || periodId === 'thisWeek' || periodId === 'year' || periodId === 'custom') {
          setPeriod(periodId);
          return; // Don't call onChange, just set the UI state
        }
        
        // For periods with submenus (month, quarter, half), select the first enabled subitem
        if (periodId === 'month' && fyReady) {
          // Find first enabled month
          const monthOptions = getMonthOptions();
          const firstEnabledMonth = monthOptions.find((m) => {
            const monthId = m.id as number;
            return !disabledSubItemsMemoized.months[monthId];
          });
          if (firstEnabledMonth) {
            setPeriod('month');
            // Note: We don't set subPeriod here as it's only used for the submenu display
            return;
          }
        } else if (periodId === 'quarter' && fyReady) {
          // Find first enabled quarter
          const firstEnabledQuarter = QUARTERS.find((q) => {
            const qId = q.id as string;
            return !disabledSubItemsMemoized.quarters[qId];
          });
          if (firstEnabledQuarter) {
            setPeriod('quarter');
            return;
          }
        } else if (periodId === 'half' && fyReady) {
          // Find first enabled half
          const firstEnabledHalf = HALVES.find((h) => {
            const hId = h.id as string;
            return !disabledSubItemsMemoized.halves[hId];
          });
          if (firstEnabledHalf) {
            setPeriod('half');
            return;
          }
        }
        
        // If no enabled subitem found, fall through to normal inference
      }
    }
    
    if (useFYFromRedux && fyReady && v.from != null && v.to != null) {
      const outOfFY = v.from < (fyStart as number) || v.to > (fyEnd as number);
      const outOfFY2 = v.from < (baseFyStart as number) || v.to > (fyEnd as number);

      
      if (outOfFY&&outOfFY2) {
        setError('Selected range is outside the current financial year.');
        return;
      }
    }
    setError(null);
    const inf = inferFromValue(value);
    setPeriod(inf.period);
    // sync local ms when not actively editing custom
    if (!(period === 'custom' && subAnchorEl)) {
      const fromMs = v.from != null ? v.from  : null;
      const toMs = v.to != null ? v.to  : null;
      setCustomMs([fromMs, toMs]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.from, value?.to, fyStart, fyEnd, useFYFromRedux, monthIdsAreCalendar, includeToday, selectedWhenNull, disabledSubItemsMemoized, fyReady, type]);

  // ---------- debounced emit for custom typing ----------
  const emit = (range: DateRange) => {
    onChange?.(normalizeRangeToSeconds(range));
  };
  const [emitDebouncedCustom, cancelEmitDebouncedCustom] = useDebouncedCallback((range: DateRange) => {
    emit(range);
  }, 400);

  // ---------- UI handlers ----------
  const openSubmenu = (parentId: ParentWithSub, anchor: HTMLElement) => {
    const subs = getSubPeriodOptions(parentId);
    setSubPeriod(subs);
    setSubAnchorEl(anchor);
    setOpenSubParent(parentId);

    const nval = normalizeRangeToSeconds(value);
    const inferred = inferSubIdForParent(parentId, nval);
    const idx =
      inferred != null
        ? Math.max(
            0,
            subs.findIndex((s) => s.id === inferred)
          )
        : 0;
    setSubIndex(idx);
    setTimeout(() => subRefs.current[idx]?.focus(), 0);
  };

  const closeSubmenu = () => {
    setSubAnchorEl(null);
    setOpenSubParent(null);
    setSubIndex(-1);
  };

  const handleMainClick = (e: React.MouseEvent<HTMLElement>) => {
    const opening = !mainAnchorEl;
    setMainAnchorEl(opening ? e.currentTarget : null);
    closeSubmenu();
    if (opening) {
      const idx = Math.max(
        0,
        menuOptions.findIndex((p) => p.id === period)
      );
      setMainIndex(idx);
      setTimeout(() => mainRefs.current[idx]?.focus(), 0);
    } else {
      setMainIndex(-1);
    }
  };

  // Custom date change (object in ms -> tuple + debounced emit in s)
  const handleCustomDateChangeMs = (rangeMs: { from: number | null; to: number | null }) => {
    rangeMs = { from: rangeMs.from ? rangeMs.from  : null, to: rangeMs.to ? rangeMs.to  : null };
    setCustomMs([rangeMs.from ?? null, rangeMs.to ?? null]);

    let from = rangeMs.from != null ? Math.floor(rangeMs.from) : null;
    let to = rangeMs.to != null ? Math.floor(rangeMs.to) : null;

    if (from != null && to != null && useFYFromRedux && fyReady) {
      from = Math.max(from, fyStart as number);
      to = Math.min(to, fyEnd as number);
    }

    setPeriod('custom');
    emitDebouncedCustom({ from, to });
  };

  const handleMainSelect = (
    option: { id: 'today' | 'tilltoday' | 'thisWeek' | 'year' | 'month' | 'quarter' | 'half' | 'custom'; label: string },
    anchor: HTMLElement
  ) => {
    if (option.id === 'today' || option.id === 'tilltoday' || option.id === 'thisWeek' || option.id === 'year') {
      setPeriod(option.id);
      const r = useFYFromRedux && fyReady ? computeRangeFY(option.id) : computeRangeGeneric(option.id);
      emit(r);
      setMainAnchorEl(null);
      setMainIndex(-1);
      closeSubmenu();
      setCustomMs(null);
      return;
    }

    if (option.id === 'custom') {
      const v = normalizeRangeToSeconds(value);
      const fromMs = v.from != null ? v.from  : null;
      const toMs = v.to != null ? v.to  : null;

      setPeriod('custom');
      setSubPeriod([]);
      setSubAnchorEl(anchor);
      setOpenSubParent(null);
      setSubIndex(-1);
      setCustomMs([fromMs, toMs]);
      return;
    }

    openSubmenu(option.id as ParentWithSub, anchor);
  };

  const handleSubSelect = (subId: string | number) => {
    const parent = openSubParent ?? (['month', 'quarter', 'half'].includes(period) ? (period as ParentWithSub) : null);

    // Check if the selected item is disabled
    let isDisabled = false;
    if (parent === 'month') {
      isDisabled = disabledSubItemsMemoized.months[subId as number] === true;
    } else if (parent === 'quarter') {
      isDisabled = disabledSubItemsMemoized.quarters[subId as string] === true;
    } else if (parent === 'half') {
      isDisabled = disabledSubItemsMemoized.halves[subId as string] === true;
    }
    if (isDisabled) return; // Prevent onChange for disabled items

    const r =
      useFYFromRedux && fyReady
        ? parent === 'month'
          ? rangeForFYMonthId(Number(subId), monthIdsAreCalendar)
          : parent === 'quarter'
          ? rangeForFYQuarter(String(subId))
          : rangeForFYHalf(String(subId))
        : computeRangeGeneric(parent ?? period, subId);

    if (parent) setPeriod(parent);

    emit(r);
    setMainAnchorEl(null);
    setMainIndex(-1);
    closeSubmenu();
    setCustomMs(null);
  };

  const handleMainKeyDown = (e: React.KeyboardEvent) => {
    if (!mainAnchorEl) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMainIndex((p) => (p + 1) % menuOptions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMainIndex((p) => (p - 1 + menuOptions.length) % menuOptions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const option = menuOptions[mainIndex];
      if (option) handleMainSelect(option as any, mainRefs.current[mainIndex]!);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      const option = menuOptions[mainIndex];
      if (!option) return;
      if (option.id === 'custom') {
        handleMainSelect(option as any, mainRefs.current[mainIndex]!);
        return;
      }
      const subs = getSubPeriodOptions(option.id);
      if (subs.length > 0) openSubmenu(option.id as ParentWithSub, mainRefs.current[mainIndex]!);
    } else if (e.key === 'Escape') {
      setMainAnchorEl(null);
      setMainIndex(-1);
      closeSubmenu();
      setCustomMs(null);
    }
  };

  const handleSubKeyDown = (e: React.KeyboardEvent) => {
    if (!subAnchorEl || !openSubParent) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSubIndex((p) => {
        let next = (p + 1) % subPeriod.length;
        // Skip disabled items
        let attempts = 0;
        while (attempts < subPeriod.length) {
          let isDisabled = false;
          if (openSubParent === 'month') {
            isDisabled = disabledSubItemsMemoized.months[subPeriod[next]?.id as number] === true;
          } else if (openSubParent === 'quarter') {
            isDisabled = disabledSubItemsMemoized.quarters[subPeriod[next]?.id as string] === true;
          } else if (openSubParent === 'half') {
            isDisabled = disabledSubItemsMemoized.halves[subPeriod[next]?.id as string] === true;
          }
          if (!isDisabled || !subPeriod[next]) break;
          next = (next + 1) % subPeriod.length;
          attempts++;
        }
        return next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSubIndex((p) => {
        let next = (p - 1 + subPeriod.length) % subPeriod.length;
        // Skip disabled items
        let attempts = 0;
        while (attempts < subPeriod.length) {
          let isDisabled = false;
          if (openSubParent === 'month') {
            isDisabled = disabledSubItemsMemoized.months[subPeriod[next]?.id as number] === true;
          } else if (openSubParent === 'quarter') {
            isDisabled = disabledSubItemsMemoized.quarters[subPeriod[next]?.id as string] === true;
          } else if (openSubParent === 'half') {
            isDisabled = disabledSubItemsMemoized.halves[subPeriod[next]?.id as string] === true;
          }
          if (!isDisabled || !subPeriod[next]) break;
          next = (next - 1 + subPeriod.length) % subPeriod.length;
          attempts++;
        }
        return next;
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = subPeriod[subIndex];
      if (opt) {
        // Check if disabled
        let isDisabled = false;
        if (openSubParent === 'month') {
          isDisabled = disabledSubItemsMemoized.months[opt.id as number] === true;
        } else if (openSubParent === 'quarter') {
          isDisabled = disabledSubItemsMemoized.quarters[opt.id as string] === true;
        } else if (openSubParent === 'half') {
          isDisabled = disabledSubItemsMemoized.halves[opt.id as string] === true;
        }
        if (!isDisabled) {
          handleSubSelect(opt.id);
        }
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      closeSubmenu();
      setTimeout(() => mainRefs.current[mainIndex]?.focus(), 0);
    } else if (e.key === 'Escape') {
      closeSubmenu();
      setCustomMs(null);
    }
  };

  useEffect(() => {
    if (mainIndex >= 0) mainRefs.current[mainIndex]?.focus();
  }, [mainIndex]);
  useEffect(() => {
    if (subIndex >= 0) subRefs.current[subIndex]?.focus();
  }, [subIndex]);

  const nval = normalizeRangeToSeconds(value);
  const dateText = formatDateRange(nval.from, nval.to, period);
  const inferredSubId = openSubParent ? inferSubIdForParent(openSubParent, nval) : undefined;

  // derived (prop) ms if not editing
  const propFromMs = nval.from != null ? nval.from  : null;
  const propToMs = nval.to != null ? nval.to  : null;

  const isOpen = Boolean(mainAnchorEl);
  const handlePrimaryMenuKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setMainAnchorEl(null);
      setMainIndex(-1);
      closeSubmenu();
      setCustomMs(null);
    }
  };
  return (
    <ClickAwayListener
      onClickAway={(event) => {
        const target = event.target as Node;
        if (mainAnchorEl?.contains(target)) return;
        if (subAnchorEl?.contains(target)) return;

        setMainAnchorEl(null);
        setMainIndex(-1);
        closeSubmenu();
        cancelEmitDebouncedCustom();
        setCustomMs(null);
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center',  flexWrap: 'wrap' }}>
        <Typography variant="subtitle1">{title}</Typography>

        <Button
          variant="outlined"
          onClick={handleMainClick}
          onKeyDown={handleMainKeyDown}
          endIcon={isOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          aria-haspopup="listbox"
          aria-expanded={isOpen ? 'true' : 'false'}
          sx={{ minWidth: 185, justifyContent: 'space-between', gap: 0.75, pr: 1.5 }}
        >
          {menuOptions.find((p) => p.id === period)?.label ?? 'Custom'}
        </Button>

        {showDateText&& dateText&& (
          <Typography variant="body2" sx={{ ml: 1, fontSize: '14px' }}>
            {dateText}
          </Typography>
        )}

        {error && (
          <Typography variant="caption" color="error" sx={{ ml: 1 }}>
            {error}
          </Typography>
        )}

        {/* Main menu */}
        <Popper open={Boolean(mainAnchorEl)} anchorEl={mainAnchorEl} sx={{ zIndex: 9999 }} placement="bottom-start" disablePortal>
          <Paper sx={{ width: mainAnchorEl?.offsetWidth }}>
            <MenuList onKeyDown={handlePrimaryMenuKeyDown}>
              {menuOptions.map((option, idx) => (
                <MenuItem
                  key={option.id}
                  ref={(el) => (mainRefs.current[idx] = el)}
                  className="simple-period-menu-item"
                  selected={option.id === period}
                  onMouseEnter={() => {
                    setMainIndex(idx);
                    mainRefs.current[idx]?.focus();
                  }}
                  onClick={(e) => handleMainSelect(option as any, e.currentTarget)}
                >
                  {option.label}
                </MenuItem>
              ))}
            </MenuList>
          </Paper>
        </Popper>

        {/* Submenu (list) */}
        {subPeriod.length > 0 && openSubParent && (
          <Popper open={Boolean(subAnchorEl)} anchorEl={subAnchorEl} sx={{ zIndex: 9999 }} placement="right-start" disablePortal>
            <Paper>
              <MenuList onKeyDown={handleSubKeyDown}>
                {subPeriod.map((sub, idx) => {
                  const isSelected = sub.id === inferredSubId;
                  // Get disabled state from memoized object based on openSubParent
                  let isDisabled = false;
                  if (openSubParent === 'month') {
                    isDisabled = disabledSubItemsMemoized.months[sub.id as number] === true;
                  } else if (openSubParent === 'quarter') {
                    isDisabled = disabledSubItemsMemoized.quarters[sub.id as string] === true;
                  } else if (openSubParent === 'half') {
                    isDisabled = disabledSubItemsMemoized.halves[sub.id as string] === true;
                  }
                  return (
                    <MenuItem
                      key={sub.id}
                      autoFocus={idx === subIndex && !isDisabled}
                      ref={(el) => (subRefs.current[idx] = el)}
                      className="simple-period-menu-item"
                      disabled={isDisabled}
                      selected={isSelected}
                      onMouseEnter={() => {
                        if (!isDisabled) {
                        setSubIndex(idx);
                        subRefs.current[idx]?.focus();
                        }
                      }}
                      onClick={(e) => {
                        if (isDisabled) {
                          e.preventDefault();
                          e.stopPropagation();
                          return false;
                        }
                        handleSubSelect(sub.id);
                      }}
                    >
                      {sub.label}
                    </MenuItem>
                  );
                })}
              </MenuList>
            </Paper>
          </Popper>
        )}

        {/* Submenu (custom date) */}
        {!openSubParent && period === 'custom' && (
          <Popper open={Boolean(subAnchorEl)} anchorEl={subAnchorEl} sx={{ zIndex: 9999 }} placement="right-start" disablePortal>
            <Paper sx={{ p: 1 }}>
              <DateRangeRsuite
                value={customMs ?? [propFromMs, propToMs]} // tuple in ms
                onChange={handleCustomDateChangeMs} // object {from,to} in ms
                shouldDisableDate={(date) => {
                  if (!fyStart || !fyEnd) return false;
                  const dateTime = date.getTime() / 1000;
                  return dateTime < fyStart || dateTime > fyEnd;
                }}
                onValidationSuccess={() => {
                  setMainAnchorEl(null);
                  setMainIndex(-1);
                  closeSubmenu();
                }}
                onOk={() => {
                  setMainAnchorEl(null);
                  setMainIndex(-1);
                  closeSubmenu();
                }}
              />
            </Paper>
          </Popper>
        )}
      </Box>
    </ClickAwayListener>
  );
};
